import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UOFM, UOFMSearchParams } from '../../services/uofmApi';

export interface UOFMState {
  // Data
  items: UOFM[];
  selectedItem: UOFM | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: UOFMSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: UOFMState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const uofmSlice = createSlice({
  name: 'uofm',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<UOFM[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<UOFM | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<UOFM>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<UOFM>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedItem?.Id === action.payload.Id) {
        state.selectedItem = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
      if (state.selectedItem?.Id === action.payload) {
        state.selectedItem = null;
      }
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<UOFMState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<UOFMSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<UOFMState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<UOFMState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetUOFMState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetUOFMState,
} = uofmSlice.actions;

// Saga action types
export const FETCH_UOFMS = 'uofm/fetchUOFMs';
export const FETCH_UOFM_BY_ID = 'uofm/fetchUOFMById';
export const CREATE_UOFM = 'uofm/createUOFM';
export const UPDATE_UOFM = 'uofm/updateUOFM';
export const DELETE_UOFM = 'uofm/deleteUOFM';
export const SEARCH_UOFMS = 'uofm/searchUOFMs';
export const FETCH_STATISTICS = 'uofm/fetchStatistics';
export const BULK_ACTIVATE = 'uofm/bulkActivate';
export const BULK_DEACTIVATE = 'uofm/bulkDeactivate';
export const BULK_DELETE = 'uofm/bulkDelete';
export const EXPORT_TO_CSV = 'uofm/exportToCSV';

// Saga action creators
export const fetchUOFMs = (params?: UOFMSearchParams) => ({ type: FETCH_UOFMS, payload: params });
export const fetchUOFMById = (id: number) => ({ type: FETCH_UOFM_BY_ID, payload: id });
export const createUOFM = (uofm: Omit<UOFM, 'Id'>) => ({ type: CREATE_UOFM, payload: uofm });
export const updateUOFM = (id: number, uofm: Partial<UOFM>) => ({ type: UPDATE_UOFM, payload: { id, uofm } });
export const deleteUOFM = (id: number) => ({ type: DELETE_UOFM, payload: id });
export const searchUOFMs = (params: UOFMSearchParams) => ({ type: SEARCH_UOFMS, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: UOFMSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default uofmSlice.reducer; 